import { useIsDesktop } from "@agrigate/shared-web-ui/lib/hooks/use-is-desktop";
import type {
    MenuItemConfig,
    MenuSeparatorConfig,
} from "@agrigate/shared-web-ui/lib/PopoverMenuCombo";
import PopoverMenuCombo from "@agrigate/shared-web-ui/lib/PopoverMenuCombo";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { compact } from "lodash";
// Header.tsx
import { type FC, type ReactNode, useMemo, useState } from "react";
import { FiUser } from "react-icons/fi";
import { RxHamburgerMenu } from "react-icons/rx";
import { usePromiseToast } from "../hooks/use-promise-toast";
import { impersonationStateAtom, sessionAtom } from "../stores/session";
import rpc from "../utils/rpc";
import GlobalSearch from "./GlobalSearch";
import SwitchUserModal from "./SwitchUserModal";

interface HeaderProps {
    title: string | undefined;
    icon?: ReactNode;
    notificationCount?: number;
    onMenuClick?: () => void;
    isMenuOpen?: boolean;
}

const ContentHeader: FC<HeaderProps> = ({
    title,
    icon,
    onMenuClick,
    isMenuOpen,
}) => {
    const [session, setSession] = useAtom(sessionAtom);
    const isAdmin = session?.roles?.includes("ADMIN");
    const isDesktop = useIsDesktop();
    const { withToast } = usePromiseToast();
    const [impersonationState, setImpersonationState] = useAtom(
        impersonationStateAtom,
    );
    const [isSwitchUserOpen, setIsSwitchUserOpen] = useState(false);

    const handleSwitchUser = async (userId: string) => {
        await withToast(
            async () => {
                const { token } = await rpc.auth.getToken({ userId });
                // Retain the original user's token across multiple switches,
                // so switching back always restores the very first user.
                const prevToken =
                    impersonationState?.prevToken ?? session?.token;
                if (!prevToken) return;
                setSession(prev => (prev ? { ...prev, token } : prev));
                setImpersonationState({
                    impersonatedUserId: userId,
                    prevToken,
                });
            },
            {
                messages: {
                    success: "Switched user successfully",
                    error: "Failed to switch user",
                },
            },
        );
    };

    const handleSwitchBackToOriginalUser = async () => {
        await withToast(
            async () => {
                if (!impersonationState) return;
                const { prevToken } = impersonationState;
                setSession(prev =>
                    prev ? { ...prev, token: prevToken } : prev,
                );
                setImpersonationState(null);
            },
            {
                messages: {
                    success: "Switched back to original user",
                    error: "Failed to switch back",
                },
            },
        );
    };

    const userInfoOptions: (MenuItemConfig | MenuSeparatorConfig)[] = useMemo(
        () =>
            compact([
                {
                    type: "item",
                    key: "email",
                    label: session?.fullName ?? "Guest",
                },
                session?.emails?.length
                    ? {
                          type: "item",
                          key: "email",
                          label: session?.emails?.[0] ?? "N/A",
                      }
                    : null,
                {
                    type: "separator",
                    key: "sep",
                },
            ]),
        [session],
    );

    const menuOptions: (MenuItemConfig | MenuSeparatorConfig)[] =
        useMemo(() => {
            if (isAdmin)
                return [
                    ...userInfoOptions,
                    {
                        type: "item",
                        key: "switchUser",
                        label: "Switch User",
                        onClick: () => setIsSwitchUserOpen(true),
                    },
                    ...(impersonationState
                        ? [
                              {
                                  type: "item" as const,
                                  key: "switchBackToOriginalUser",
                                  label: "Switch back to original user",
                                  onClick: handleSwitchBackToOriginalUser,
                              },
                          ]
                        : []),
                    {
                        type: "item",
                        key: "logout",
                        label: "Logout",
                        onClick: () => {
                            setSession(null);
                            if (LOGOUT_URL) {
                                location.href = LOGOUT_URL;
                            }
                        },
                    },
                ];
            else
                return [
                    ...userInfoOptions,
                    ...(impersonationState
                        ? [
                              {
                                  type: "item" as const,
                                  key: "switchBackToOriginalUser",
                                  label: "Switch back to original user",
                                  onClick: handleSwitchBackToOriginalUser,
                              },
                          ]
                        : []),
                    {
                        type: "item",
                        key: "deleteSelf",
                        label: "Delete Account",
                        onClick: () => {
                            withToast(
                                async () => {
                                    await rpc.users.deleteMany({
                                        userIds: ["$self"],
                                    });
                                    setSession(null);
                                },
                                {
                                    messages: {
                                        success: "Account deleted successfully",
                                        error: "Failed to process your request",
                                    },
                                },
                            );
                        },
                    },
                    {
                        type: "item",
                        key: "logout",
                        label: "Logout",
                        onClick: () => {
                            setSession(null);
                        },
                    },
                ];
        }, [
            isAdmin,
            userInfoOptions,
            impersonationState,
            handleSwitchBackToOriginalUser,
        ]);

    return (
        <Flex
            as="header"
            direction="column"
            borderBottomWidth={1}
            borderColor="gray.100"
            backgroundColor={"bg"}
            flexShrink={0}
            className="screen-only"
        >
            <Flex align="center" justify="space-between" px={4} height="50px">
                <Flex align="center">
                    {!isDesktop && (
                        <Button
                            variant="outline"
                            p={2}
                            mr={2}
                            onClick={onMenuClick}
                        >
                            <RxHamburgerMenu />
                        </Button>
                    )}

                    {icon && <Box mr={2}>{icon}</Box>}
                    <Text fontSize="sm" fontWeight="bold" textAlign="left">
                        {title ?? "Dashboard"}
                    </Text>
                </Flex>
                {isDesktop && <GlobalSearch />}
                <Flex gap={2} alignItems="center">
                    {/* <NotificationBadge
                        badgeColor="red.500"
                        hasNotification
                        iconSize={34}
                        count={99}
                    />
                    <Avatar
                        size={"s"}
                        src={ProfileAvatarIcon}
                    /> */}
                    <PopoverMenuCombo
                        size="sm"
                        variant="plain"
                        label={
                            <Box
                                borderRadius="15px"
                                p="5px"
                                border="1px solid var(--chakra-colors-gray-500)"
                            >
                                <FiUser />
                            </Box>
                        }
                        options={menuOptions}
                    />
                </Flex>
            </Flex>
            {!isDesktop && !isMenuOpen && (
                <Flex px={4} pb={2} width="100%">
                    <GlobalSearch width="100%" />
                </Flex>
            )}
            <SwitchUserModal
                isModalOpen={isSwitchUserOpen}
                closeModal={() => setIsSwitchUserOpen(false)}
                handleSwitchUser={handleSwitchUser}
            />
        </Flex>
    );
};

export default ContentHeader;
